// FigureTile — one honest figure: label + coverage value (+ denominator/unit) +
// per-tile freshness dot (GAP-7 fallback to card-level) + low-n disclaim +
// optional breakdown. No card layout — a composable atom.
import React from "react";

import type { Figure, Freshness } from "../../../chatBot/models/machineEvidenceModels";
import {
  isLowSample,
  resolveBreakdown,
  resolveCoverage,
  resolveFigureFreshness,
} from "./honestyEngine";
import useTranslation from "../../../hooks/useTranslation";
import { dt } from "../../../themes/redesignTokens";
import { FRESHNESS_COLORS } from "./honestyTokens";
import { CoverageValue } from "./CoverageValue";
import { Breakdown } from "./Breakdown";

export interface FigureTileProps {
  figure: Figure;
  /** card-level freshness — the per-tile dot falls back to this (GAP-7). */
  cardFreshness: Freshness | null | undefined;
  /** show the grouped breakdown under the value. */
  showBreakdown?: boolean;
  /** display the figure's `measure_basis` (commanded/measured/…) when present.
   *  Null on MASH (GAP-11); shown for SBL500 live-PLC pips. Rendered VERBATIM as a
   *  technical basis token (a closed contract enum), not translated. */
  showMeasureBasis?: boolean;
}

export const FigureTile: React.FC<FigureTileProps> = ({
  figure,
  cardFreshness,
  showBreakdown,
  showMeasureBasis,
}) => {
  const { translate } = useTranslation("machineEvidence");
  const coverage = resolveCoverage(figure);
  const freshness = resolveFigureFreshness(figure, cardFreshness);
  const dotTone = FRESHNESS_COLORS[freshness.state];
  const lowN = isLowSample(figure.n);
  const breakdown = showBreakdown ? resolveBreakdown(figure.breakdown) : null;

  return (
    <div className="msc-tile" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          aria-hidden
          title={`Feed: ${freshness.state}`}
          style={{ width: 6, height: 6, borderRadius: "50%", background: dotTone.fg, flex: "0 0 auto" }}
        />
        <span style={{ fontSize: 12, color: dt.textSecondary }}>{figure.label}</span>
        {showMeasureBasis && figure.measure_basis ? (
          <span
            className="msc-measure-basis"
            style={{
              fontFamily: dt.fontMono,
              fontSize: 10,
              color: dt.textMuted,
              padding: "0 5px",
              borderRadius: dt.radiusPill,
              border: `1px solid ${dt.cardBorder}`,
            }}
          >
            {figure.measure_basis}
          </span>
        ) : null}
      </div>
      <CoverageValue
        resolution={coverage}
        unit={figure.unit}
        denominator={figure.denominator}
        dim={freshness.dim}
      />
      {lowN ? (
        <span style={{ fontSize: 11, color: dt.textMuted, fontStyle: "italic" }}>
          {translate("figure.lowSample", {
            n: figure.n,
            defaultValue: "low sample (n={{n}})",
          }) as string}
        </span>
      ) : null}
      {breakdown ? <Breakdown resolution={breakdown} /> : null}
    </div>
  );
};

export default FigureTile;
